export type MimeTypes =
  // Documents
  | 'application/pdf'
  | 'application/msword'
  | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  | 'application/vnd.ms-excel'
  | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  | 'application/vnd.ms-powerpoint'
  | 'application/vnd.openxmlformats-officedocument.presentationml.presentation'

  // Text
  | 'text/plain'
  | 'text/html'
  | 'text/css'
  | 'text/javascript'
  | 'text/csv'
  | 'application/json'
  | 'application/xml'

  // Image
  | 'image/jpeg'
  | 'image/png'
  | 'image/gif'
  | 'image/svg+xml'
  | 'image/webp'
  | 'image/bmp'
  | 'image/tiff'

  // Audio
  | 'audio/mpeg'
  | 'audio/wav'
  | 'audio/ogg'
  | 'audio/aac'
  | 'audio/flac'

  // Video
  | 'video/mp4'
  | 'video/avi'
  | 'video/mkv'
  | 'video/mov'
  | 'video/webm'
  | 'video/wmv'

  // Archive
  | 'application/zip'
  | 'application/x-rar-compressed'
  | 'application/x-7z-compressed'
  | 'application/x-tar'
  | 'application/gzip';

// Document MIME type guards
export const isApplicationPdfFile = (value: unknown): value is File & { type: 'application/pdf' } =>
  value instanceof File && value.type === 'application/pdf';

export const isApplicationMswordFile = (
  value: unknown,
): value is File & { type: 'application/msword' } =>
  value instanceof File && value.type === 'application/msword';

export const isApplicationWordDocumentFile = (
  value: unknown,
): value is File & {
  type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
} =>
  value instanceof File &&
  value.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const isApplicationMsExcelFile = (
  value: unknown,
): value is File & { type: 'application/vnd.ms-excel' } =>
  value instanceof File && value.type === 'application/vnd.ms-excel';

export const isApplicationExcelSheetFile = (
  value: unknown,
): value is File & { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' } =>
  value instanceof File &&
  value.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const isApplicationMsPowerpointFile = (
  value: unknown,
): value is File & { type: 'application/vnd.ms-powerpoint' } =>
  value instanceof File && value.type === 'application/vnd.ms-powerpoint';

export const isApplicationPowerpointPresentationFile = (
  value: unknown,
): value is File & {
  type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
} =>
  value instanceof File &&
  value.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

// Text MIME type guards
export const isTextPlainFile = (value: unknown): value is File & { type: 'text/plain' } =>
  value instanceof File && value.type === 'text/plain';

export const isTextHtmlFile = (value: unknown): value is File & { type: 'text/html' } =>
  value instanceof File && value.type === 'text/html';

export const isTextCssFile = (value: unknown): value is File & { type: 'text/css' } =>
  value instanceof File && value.type === 'text/css';

export const isTextJavascriptFile = (value: unknown): value is File & { type: 'text/javascript' } =>
  value instanceof File && value.type === 'text/javascript';

export const isTextCsvFile = (value: unknown): value is File & { type: 'text/csv' } =>
  value instanceof File && value.type === 'text/csv';

export const isApplicationJsonFile = (
  value: unknown,
): value is File & { type: 'application/json' } =>
  value instanceof File && value.type === 'application/json';

export const isApplicationXmlFile = (value: unknown): value is File & { type: 'application/xml' } =>
  value instanceof File && value.type === 'application/xml';

// Image MIME type guards
export const isImageJpegFile = (value: unknown): value is File & { type: 'image/jpeg' } =>
  value instanceof File && value.type === 'image/jpeg';

export const isImagePngFile = (value: unknown): value is File & { type: 'image/png' } =>
  value instanceof File && value.type === 'image/png';

export const isImageGifFile = (value: unknown): value is File & { type: 'image/gif' } =>
  value instanceof File && value.type === 'image/gif';

export const isImageSvgXmlFile = (value: unknown): value is File & { type: 'image/svg+xml' } =>
  value instanceof File && value.type === 'image/svg+xml';

export const isImageWebpFile = (value: unknown): value is File & { type: 'image/webp' } =>
  value instanceof File && value.type === 'image/webp';

export const isImageBmpFile = (value: unknown): value is File & { type: 'image/bmp' } =>
  value instanceof File && value.type === 'image/bmp';

export const isImageTiffFile = (value: unknown): value is File & { type: 'image/tiff' } =>
  value instanceof File && value.type === 'image/tiff';

// Audio MIME type guards
export const isAudioMpegFile = (value: unknown): value is File & { type: 'audio/mpeg' } =>
  value instanceof File && value.type === 'audio/mpeg';

export const isAudioWavFile = (value: unknown): value is File & { type: 'audio/wav' } =>
  value instanceof File && value.type === 'audio/wav';

export const isAudioOggFile = (value: unknown): value is File & { type: 'audio/ogg' } =>
  value instanceof File && value.type === 'audio/ogg';

export const isAudioAacFile = (value: unknown): value is File & { type: 'audio/aac' } =>
  value instanceof File && value.type === 'audio/aac';

export const isAudioFlacFile = (value: unknown): value is File & { type: 'audio/flac' } =>
  value instanceof File && value.type === 'audio/flac';

// Video MIME type guards
export const isVideoMp4File = (value: unknown): value is File & { type: 'video/mp4' } =>
  value instanceof File && value.type === 'video/mp4';

export const isVideoAviFile = (value: unknown): value is File & { type: 'video/avi' } =>
  value instanceof File && value.type === 'video/avi';

export const isVideoMkvFile = (value: unknown): value is File & { type: 'video/mkv' } =>
  value instanceof File && value.type === 'video/mkv';

export const isVideoMovFile = (value: unknown): value is File & { type: 'video/mov' } =>
  value instanceof File && value.type === 'video/mov';

export const isVideoWebmFile = (value: unknown): value is File & { type: 'video/webm' } =>
  value instanceof File && value.type === 'video/webm';

export const isVideoWmvFile = (value: unknown): value is File & { type: 'video/wmv' } =>
  value instanceof File && value.type === 'video/wmv';

// Archive MIME type guards
export const isApplicationZipFile = (value: unknown): value is File & { type: 'application/zip' } =>
  value instanceof File && value.type === 'application/zip';

export const isApplicationRarCompressedFile = (
  value: unknown,
): value is File & { type: 'application/x-rar-compressed' } =>
  value instanceof File && value.type === 'application/x-rar-compressed';

export const isApplication7zCompressedFile = (
  value: unknown,
): value is File & { type: 'application/x-7z-compressed' } =>
  value instanceof File && value.type === 'application/x-7z-compressed';

export const isApplicationTarFile = (
  value: unknown,
): value is File & { type: 'application/x-tar' } =>
  value instanceof File && value.type === 'application/x-tar';

export const isApplicationGzipFile = (
  value: unknown,
): value is File & { type: 'application/gzip' } =>
  value instanceof File && value.type === 'application/gzip';

// Group type guards
export const isDocumentFile = (
  value: unknown,
): value is
  | (File & { type: 'application/pdf' })
  | (File & { type: 'application/msword' })
  | (File & { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
  | (File & { type: 'application/vnd.ms-excel' })
  | (File & { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  | (File & { type: 'application/vnd.ms-powerpoint' })
  | (File & {
      type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    }) =>
  isApplicationPdfFile(value) ||
  isApplicationMswordFile(value) ||
  isApplicationWordDocumentFile(value) ||
  isApplicationMsExcelFile(value) ||
  isApplicationExcelSheetFile(value) ||
  isApplicationMsPowerpointFile(value) ||
  isApplicationPowerpointPresentationFile(value);

export const isTextFile = (
  value: unknown,
): value is
  | (File & { type: 'text/plain' })
  | (File & { type: 'text/html' })
  | (File & { type: 'text/css' })
  | (File & { type: 'text/javascript' })
  | (File & { type: 'text/csv' })
  | (File & { type: 'application/json' })
  | (File & { type: 'application/xml' }) =>
  isTextPlainFile(value) ||
  isTextHtmlFile(value) ||
  isTextCssFile(value) ||
  isTextJavascriptFile(value) ||
  isTextCsvFile(value) ||
  isApplicationJsonFile(value) ||
  isApplicationXmlFile(value);

export const isImageFile = (
  value: unknown,
): value is
  | (File & { type: 'image/jpeg' })
  | (File & { type: 'image/png' })
  | (File & { type: 'image/gif' })
  | (File & { type: 'image/svg+xml' })
  | (File & { type: 'image/webp' })
  | (File & { type: 'image/bmp' })
  | (File & { type: 'image/tiff' }) =>
  isImageJpegFile(value) ||
  isImagePngFile(value) ||
  isImageGifFile(value) ||
  isImageSvgXmlFile(value) ||
  isImageWebpFile(value) ||
  isImageBmpFile(value) ||
  isImageTiffFile(value);

export const isAudioFile = (
  value: unknown,
): value is
  | (File & { type: 'audio/mpeg' })
  | (File & { type: 'audio/wav' })
  | (File & { type: 'audio/ogg' })
  | (File & { type: 'audio/aac' })
  | (File & { type: 'audio/flac' }) =>
  isAudioMpegFile(value) ||
  isAudioWavFile(value) ||
  isAudioOggFile(value) ||
  isAudioAacFile(value) ||
  isAudioFlacFile(value);

export const isVideoFile = (
  value: unknown,
): value is
  | (File & { type: 'video/mp4' })
  | (File & { type: 'video/avi' })
  | (File & { type: 'video/mkv' })
  | (File & { type: 'video/mov' })
  | (File & { type: 'video/webm' })
  | (File & { type: 'video/wmv' }) =>
  isVideoMp4File(value) ||
  isVideoAviFile(value) ||
  isVideoMkvFile(value) ||
  isVideoMovFile(value) ||
  isVideoWebmFile(value) ||
  isVideoWmvFile(value);

export const isArchiveFile = (
  value: unknown,
): value is
  | (File & { type: 'application/zip' })
  | (File & { type: 'application/x-rar-compressed' })
  | (File & { type: 'application/x-7z-compressed' })
  | (File & { type: 'application/x-tar' })
  | (File & { type: 'application/gzip' }) =>
  isApplicationZipFile(value) ||
  isApplicationRarCompressedFile(value) ||
  isApplication7zCompressedFile(value) ||
  isApplicationTarFile(value) ||
  isApplicationGzipFile(value);

/**
 * Maps MIME types to their corresponding file extensions.
 * This is used to improve file picker compatibility on Windows,
 * where the `accept` attribute may not work correctly with MIME types alone.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/accept
 * @see https://stackoverflow.com/questions/181214/file-input-accept-attribute-is-it-useful
 */
export const MIME_EXTENSION_MAP: Readonly<Record<MimeTypes, readonly string[]>> = {
  // Documents
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],

  // Text
  'text/plain': ['.txt'],
  'text/html': ['.html', '.htm'],
  'text/css': ['.css'],
  'text/javascript': ['.js', '.mjs'],
  'text/csv': ['.csv'],
  'application/json': ['.json'],
  'application/xml': ['.xml'],

  // Image - JPEG has multiple extensions due to Windows compatibility issues
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/svg+xml': ['.svg', '.svgz'],
  'image/webp': ['.webp'],
  'image/bmp': ['.bmp'],
  'image/tiff': ['.tif', '.tiff'],

  // Audio
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/ogg': ['.ogg', '.oga'],
  'audio/aac': ['.aac'],
  'audio/flac': ['.flac'],

  // Video
  'video/mp4': ['.mp4', '.m4v'],
  'video/avi': ['.avi'],
  'video/mkv': ['.mkv'],
  'video/mov': ['.mov'],
  'video/webm': ['.webm'],
  'video/wmv': ['.wmv'],

  // Archive
  'application/zip': ['.zip'],
  'application/x-rar-compressed': ['.rar'],
  'application/x-7z-compressed': ['.7z'],
  'application/x-tar': ['.tar'],
  'application/gzip': ['.gz', '.gzip'],
};
